import type { GenerationStats, LayerStats, PfpProject, Tier } from "./types";
import { mulberry32 } from "./internals";
import { selectOnce } from "./selection";

function tierFor(p: number): Tier {
  if (p < 0.01) return "legendary";
  if (p < 0.05) return "rare";
  if (p < 0.15) return "uncommon";
  return "common";
}

/** Count how often each trait would appear over `samples` rolls. */
export function rarityPreview(
  project: PfpProject,
  samples = 1000,
  seed?: number,
): Map<string, number> {
  const rand = seed !== undefined ? mulberry32(seed) : Math.random;
  const counts = new Map<string, number>();
  for (let i = 0; i < samples; i++) {
    const sel = selectOnce(project, rand);
    if (!sel) continue;
    for (const [layerId, traitId] of Object.entries(sel)) {
      const k = `${layerId}:${traitId}`;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
  }
  return counts;
}

/**
 * Run the generator `samples` times against the current project to project
 * what an actual mint batch of `mintCount` tokens would look like:
 * trait frequencies, rarest combos, unreachable traits, rule-tightness warnings.
 *
 * Pure function — safe to call on every keystroke (memoize at the React layer).
 */
export function analyzeProject(
  project: PfpProject,
  mintCount: number,
  samples = 1000,
  seed?: number,
): GenerationStats {
  const rand = seed !== undefined ? mulberry32(seed) : Math.random;
  const totalPossibleCombos = project.layers.reduce(
    (acc, l) => acc * Math.max(1, l.traits.length),
    project.layers.length === 0 ? 0 : 1,
  );

  const counts = new Map<string, number>();
  const comboCounts = new Map<string, { count: number; combo: Record<string, string> }>();
  let attempts = 0;
  let successes = 0;
  const maxAttempts = samples * 5;
  while (successes < samples && attempts < maxAttempts) {
    attempts++;
    const sel = selectOnce(project, rand);
    if (!sel) continue;
    successes++;
    for (const [layerId, traitId] of Object.entries(sel)) {
      const k = `${layerId}:${traitId}`;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    const key = JSON.stringify(sel);
    const ex = comboCounts.get(key);
    if (ex) ex.count++;
    else comboCounts.set(key, { count: 1, combo: sel });
  }

  const successRate = attempts === 0 ? 0 : successes / attempts;
  const denom = Math.max(1, successes);

  const perLayer: LayerStats[] = project.layers.map((layer) => ({
    layerId: layer.id,
    layerName: layer.name,
    traits: layer.traits.map((trait) => {
      const count = counts.get(`${layer.id}:${trait.id}`) ?? 0;
      const probability = count / denom;
      return {
        traitId: trait.id,
        traitName: trait.name,
        probability,
        expectedCount: Math.round(probability * mintCount),
        tier: tierFor(probability),
      };
    }),
  }));

  const rarestCombos = Array.from(comboCounts.values())
    .sort((a, b) => a.count - b.count)
    .slice(0, 5)
    .map(({ count, combo }) => {
      const probability = count / denom;
      const labels: Record<string, string> = {};
      for (const layer of project.layers) {
        const traitId = combo[layer.id];
        if (!traitId) continue;
        const trait = layer.traits.find((t) => t.id === traitId);
        if (trait) labels[layer.name] = trait.name;
      }
      return {
        combo,
        comboLabels: labels,
        probability,
        expectedCount: Math.round(probability * mintCount),
      };
    });

  const unreachableTraits = [];
  for (const layer of project.layers) {
    for (const trait of layer.traits) {
      if ((counts.get(`${layer.id}:${trait.id}`) ?? 0) === 0) {
        unreachableTraits.push({
          layerId: layer.id,
          traitId: trait.id,
          layerName: layer.name,
          traitName: trait.name,
        });
      }
    }
  }

  const warnings: string[] = [];
  if (project.layers.length === 0) {
    warnings.push("No layers yet.");
  } else if (project.layers.some((l) => l.traits.length === 0)) {
    warnings.push("At least one layer has no traits — generation will fail.");
  }
  if (successRate < 0.5 && project.layers.length > 0) {
    warnings.push(
      `Only ${(successRate * 100).toFixed(0)}% of attempts produced a valid token — rules may be too strict.`,
    );
  }
  if (unreachableTraits.length > 0) {
    warnings.push(
      `${unreachableTraits.length} trait${unreachableTraits.length === 1 ? "" : "s"} never appear in samples — blocked by rules.`,
    );
  }
  if (totalPossibleCombos > 0 && mintCount > 0 && totalPossibleCombos < mintCount * 1.5) {
    warnings.push(
      `Only ${totalPossibleCombos} unique combinations possible — generating ${mintCount} tokens may run out before deduping.`,
    );
  }

  return {
    sampleSize: successes,
    successRate,
    totalPossibleCombos,
    effectiveCombos: comboCounts.size,
    expectedTokens: mintCount,
    perLayer,
    rarestCombos,
    warnings,
    unreachableTraits,
  };
}
